/**
 * PURPOSE: Lista maestra de pruebas de Atletismo (columna izquierda).
 * RESPONSIBILITIES: Búsqueda tolerante, filtros rápidos (nivel, rama, tipo, estado, jornada),
 *   orden por día y hora de la próxima fase (agrupado por jornada; oficializadas al final) y una fila compacta por prueba con estado, horario, avance,
 *   visibilidad, advertencias y DEMO. Las combinadas (decatlón, heptatlón) tienen un acordeón "Ver pruebas" con sus
 *   pruebas asociadas (`events` del resumen, o del detalle si el catálogo no las trae), cada una con estado, horario y
 *   avance: al elegir una se abre esa sub-prueba directamente.
 *   FINISHLYNX DE VARIAS PRUEBAS: el ícono "Seleccionar para FinishLynx" muestra casillas en la lista, "Seleccionar las
 *   visibles" respeta filtros y búsqueda (con una jornada elegida, solo las fases de esa jornada), el contador dice
 *   "N pruebas · M series" y "Descargar .zip" baja lynx.evt + lynx.sch unidos. Las pruebas que no van a FinishLynx
 *   (campo, DEMO, sin series de pista) no se marcan y el tooltip dice por qué.
 * COLLABORATORS: AtletismoResultadosArea.jsx, utils/athleticsSearch.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/PruebaListPanel.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  FiAlertTriangle,
  FiCheckSquare,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiLayers,
  FiList,
  FiSearch,
  FiX,
} from "react-icons/fi";

import {
  BRANCH_OPTIONS,
  DISCIPLINE_OPTIONS,
  EMPTY_PRUEBA_FILTERS,
  LEVEL_OPTIONS,
  PRUEBA_STATUS_OPTIONS,
  filterPruebas,
  groupPruebasBySchedule,
  hasActivePruebaFilters,
  pickNextSchedule,
} from "../../../utils/athleticsSearch.js";
import { useAthleticsPrueba } from "../../../hooks/useAthleticsResults.js";
import { downloadAthleticsLynxZip, saveAthleticsFile } from "../../../services/athleticsResultsService.js";
import { parseAthleticsError } from "../../../utils/athleticsResultsErrors.js";
import { RESULTS_VISIBILITY_LABELS, formatTime } from "../labels.js";
import { lynxBlockReason, summarizeLynxSelection, unitsForSelection } from "../lynxSelection.js";
import { IconAction } from "./IconAction.jsx";
import {
  Badge,
  DemoBadge,
  EmptyState,
  ErrorState,
  LoadingState,
  PruebaStatusChip,
  Spinner,
  selectClass,
} from "./ui.jsx";

const VisibilityIcons = ({ visibility }) => {
  const startList = Boolean(visibility?.startList);
  const results = visibility?.results || "off";
  const resultsTone =
    results === "live" ? "text-amber-600" : results === "official" ? "text-emerald-600" : "text-slate-300";
  const ResultsIcon = results === "off" ? FiEyeOff : FiEye;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        title={startList ? "Lista de salida pública" : "Lista de salida oculta"}
        className={startList ? "text-indigo-600" : "text-slate-300"}
      >
        <FiList aria-hidden="true" />
        <span className="sr-only">{startList ? "Lista de salida pública" : "Lista de salida oculta"}</span>
      </span>
      <span title={RESULTS_VISIBILITY_LABELS[results] || results} className={resultsTone}>
        <ResultsIcon aria-hidden="true" />
        <span className="sr-only">{RESULTS_VISIBILITY_LABELS[results] || results}</span>
      </span>
    </span>
  );
};

const SUB_STATUS_DOT = { pending: "bg-slate-300", in_progress: "bg-amber-500", official: "bg-emerald-500" };
const SUB_STATUS_TITLE = { pending: "Pendiente", in_progress: "En curso", official: "Oficial" };

/** Sub-pruebas tomadas del detalle de la combinada (cuando el catálogo no trae `events`). */
const eventsFromDetail = (detail) =>
  (Array.isArray(detail?.combined?.events) ? detail.combined.events : []).map((event) => ({
    id: event.pruebaId,
    key: event.key,
    name: event.name,
    order: event.order,
    status: event.status,
    schedule: event.schedule,
    progress: null,
  }));

/** Pruebas asociadas de una combinada, como subtareas: acceso directo a cada una. */
const CombinedEventsList = ({ id, parentId, events, selectedSubId, parentSelected, onSelectSub }) => (
  <ul id={id} className="ml-4 space-y-0.5 border-l-2 border-indigo-100 pl-2">
    {events.map((event) => {
      const active = parentSelected && Number(selectedSubId) === Number(event.id);
      const entries = Number(event.progress?.entries) || 0;
      const withResult = Number(event.progress?.withResult) || 0;
      // Texto completo al pasar el mouse: en paneles angostos el nombre se corta con "…".
      const hoverText = [
        `${event.order}. ${event.name}`,
        event.schedule?.time
          ? `${event.schedule.jornada ? `Jornada ${event.schedule.jornada} · ` : ""}${formatTime(event.schedule.time)}`
          : null,
        event.progress ? `${withResult}/${entries} con resultado` : null,
        SUB_STATUS_TITLE[event.status] || null,
      ]
        .filter(Boolean)
        .join(" · ");
      return (
        <li key={event.id}>
          <button
            type="button"
            title={hoverText}
            onClick={() => onSelectSub(parentId, event.id)}
            aria-current={active ? "true" : undefined}
            className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition ${
              active ? "bg-indigo-600 text-white" : "text-slate-700 hover:bg-white"
            }`}
          >
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${SUB_STATUS_DOT[event.status] || SUB_STATUS_DOT.pending}`}
              title={SUB_STATUS_TITLE[event.status] || event.status}
              aria-hidden="true"
            />
            <span className={`w-4 shrink-0 text-[11px] font-black ${active ? "text-indigo-200" : "text-slate-400"}`}>
              {event.order}.
            </span>
            <span className="min-w-0 flex-1 truncate font-semibold">{event.name}</span>
            {event.schedule?.time ? (
              <span className={`shrink-0 text-[11px] ${active ? "text-indigo-100" : "text-slate-400"}`}>
                {event.schedule.jornada ? `J${event.schedule.jornada} · ` : ""}
                {formatTime(event.schedule.time)}
              </span>
            ) : null}
            {event.progress ? (
              <span
                className={`shrink-0 text-[11px] font-semibold ${
                  active ? "text-indigo-100" : entries > 0 && withResult >= entries ? "text-emerald-600" : "text-slate-400"
                }`}
                title="Inscritos con resultado / inscritos"
              >
                {withResult}/{entries}
              </span>
            ) : null}
            <span className="sr-only">{SUB_STATUS_TITLE[event.status] || event.status}</span>
          </button>
        </li>
      );
    })}
  </ul>
);

/**
 * Acordeón de una combinada: botón "Ver pruebas" bajo la tarjeta y, al abrirlo, sus pruebas asociadas. Si el catálogo
 * no trae `events` (backend anterior), las pide al detalle de la combinada al abrir.
 */
const CombinedAccordion = ({ prueba, idevent, idsport, expanded, onToggle, selected, selectedSubId, onSelectSub }) => {
  const own = Array.isArray(prueba.events) ? prueba.events : null;
  const detailQuery = useAthleticsPrueba({ idevent, idsport, pruebaId: prueba.id, enabled: Boolean(expanded) && !own });
  const events = own || eventsFromDetail(detailQuery.data);
  const listId = `ath-combined-${prueba.id}`;
  const count = events.length;
  return (
    <>
      <button
        type="button"
        onClick={() => onToggle(prueba.id)}
        aria-expanded={Boolean(expanded)}
        aria-controls={listId}
        className={`flex w-full items-center justify-between gap-2 border-t px-3 py-1.5 text-xs font-bold text-indigo-700 transition ${
          selected ? "border-indigo-200 hover:bg-indigo-100/70" : "border-slate-200 hover:bg-indigo-50"
        }`}
      >
        <span className="inline-flex items-center gap-1.5">
          <FiLayers aria-hidden="true" />
          {expanded ? "Ocultar pruebas" : `Ver pruebas${count ? ` (${count})` : ""}`}
        </span>
        {expanded ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
      </button>
      {expanded ? (
        <div className="px-2 pb-2 pt-1">
          {!own && detailQuery.isLoading ? (
            <p className="px-2 py-1 text-xs italic text-slate-400">Cargando pruebas…</p>
          ) : events.length ? (
            <CombinedEventsList
              id={listId}
              parentId={prueba.id}
              events={events}
              selectedSubId={selectedSubId}
              parentSelected={selected}
              onSelectSub={onSelectSub}
            />
          ) : (
            <p className="px-2 py-1 text-xs italic text-slate-400">Sin pruebas asociadas.</p>
          )}
        </div>
      ) : null}
    </>
  );
};

const PruebaListItem = ({
  prueba,
  selected,
  onSelect,
  jornadaLabels,
  expanded,
  onToggle,
  selectedSubId,
  onSelectSub,
  idevent,
  idsport,
  lynx = null,
}) => {
  const next = pickNextSchedule(prueba.schedule);
  const entries = Number(prueba.progress?.entries) || 0;
  const withResult = Number(prueba.progress?.withResult) || 0;
  const complete = entries > 0 && withResult >= entries;
  const jornadaTitle = next ? jornadaLabels.get(String(next.jornada)) : undefined;
  const isCombined = prueba.format === "PCB";
  const cardClass = isCombined
    ? `w-full px-3 py-2.5 text-left transition ${selected ? "" : "hover:bg-slate-50"}`
    : `w-full rounded-xl border px-3 py-2.5 text-left transition ${
        selected ? "border-indigo-300 bg-indigo-50 shadow-sm" : "border-transparent hover:border-slate-200 hover:bg-slate-50"
      }`;

  const card = (
    <button
      type="button"
      onClick={() => onSelect(prueba.id)}
      aria-current={selected && !(isCombined && selectedSubId) ? "true" : undefined}
      className={cardClass}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={`text-sm font-bold leading-5 ${selected ? "text-indigo-800" : "text-slate-800"}`}>
          {prueba.label || prueba.name}
        </span>
        <PruebaStatusChip status={prueba.status} />
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
        {next ? (
          <span className="inline-flex items-center gap-1" title={jornadaTitle}>
            <FiClock aria-hidden="true" />
            {next.jornada ? `J${next.jornada} · ` : ""}
            {next.label || ""} {formatTime(next.time)}
          </span>
        ) : (
          <span className="italic">Sin horario</span>
        )}
        <span
          className={`font-semibold ${complete ? "text-emerald-600" : "text-slate-500"}`}
          title="Inscritos con resultado / inscritos"
        >
          {withResult}/{entries}
        </span>
        <VisibilityIcons visibility={prueba.visibility} />
        {Number(prueba.warningsCount) > 0 ? (
          <Badge tone="amber" icon={FiAlertTriangle} title="Advertencias de inscripción">
            {prueba.warningsCount}
          </Badge>
        ) : null}
        {prueba.isDemo ? <DemoBadge /> : null}
      </div>
    </button>
  );

  const item = !isCombined ? (
    card
  ) : (
    <div
      className={`overflow-hidden rounded-xl border transition ${
        selected ? "border-indigo-300 bg-indigo-50 shadow-sm" : "border-slate-200 bg-white"
      }`}
    >
      {card}
      <CombinedAccordion
        prueba={prueba}
        idevent={idevent}
        idsport={idsport}
        expanded={expanded}
        onToggle={onToggle}
        selected={selected}
        selectedSubId={selectedSubId}
        onSelectSub={onSelectSub}
      />
    </div>
  );
  if (!lynx) return item;
  // Selección FinishLynx: casilla a la izquierda (deshabilitada con el motivo si la prueba no va a FinishLynx).
  const label = prueba.label || prueba.name;
  return (
    <div className="flex items-start gap-1.5">
      <input
        type="checkbox"
        checked={lynx.checked}
        disabled={Boolean(lynx.reason)}
        onChange={() => lynx.onToggle(prueba)}
        aria-label={`Seleccionar ${label} para FinishLynx`}
        title={lynx.reason || `Incluir ${label} en el .zip de FinishLynx`}
        className="mt-3 h-4 w-4 shrink-0 cursor-pointer accent-emerald-600 disabled:cursor-not-allowed disabled:opacity-40"
      />
      <div className="min-w-0 flex-1">{item}</div>
    </div>
  );
};

const FilterSelect = ({ id, label, value, onChange, options, allLabel }) => (
  <label htmlFor={id} className="flex min-w-0 flex-col gap-1">
    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</span>
    <select id={id} value={value} onChange={(event) => onChange(event.target.value)} className={selectClass}>
      <option value="">{allLabel}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const PruebaListPanel = ({
  listQuery,
  filters,
  onFiltersChange,
  selectedId,
  onSelect,
  selectedSubId = null,
  onSelectSub,
  idevent,
  idsport,
}) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // La combinada elegida se despliega sola para ver sus pruebas.
  useEffect(() => {
    if (selectedId === null || selectedId === undefined) return;
    setExpandedIds((current) =>
      current.has(Number(selectedId)) ? current : new Set([...current, Number(selectedId)])
    );
  }, [selectedId]);
  const toggleExpanded = (id) =>
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(Number(id))) next.delete(Number(id));
      else next.add(Number(id));
      return next;
    });

  const pruebas = useMemo(() => listQuery.data?.pruebas || [], [listQuery.data]);
  const jornadas = useMemo(() => listQuery.data?.jornadas || [], [listQuery.data]);
  const jornadaLabels = useMemo(
    () => new Map(jornadas.map((jornada) => [String(jornada.no), jornada.label])),
    [jornadas]
  );
  const jornadaOptions = useMemo(
    () => jornadas.map((jornada) => ({ value: String(jornada.no), label: jornada.label || `Jornada ${jornada.no}` })),
    [jornadas]
  );
  const filtered = useMemo(() => filterPruebas(pruebas, filters), [pruebas, filters]);
  const groups = useMemo(() => groupPruebasBySchedule(filtered, jornadaLabels), [filtered, jornadaLabels]);
  const activeFilters = hasActivePruebaFilters(filters);

  const setFilter = (key, value) => onFiltersChange({ ...filters, [key]: value });

  // FinishLynx de varias pruebas: prueba → hojas marcadas (se fijan al marcar, con la jornada del filtro de ese momento).
  const [lynxMode, setLynxMode] = useState(false);
  const [lynxSelection, setLynxSelection] = useState(() => new Map());
  const [lynxBusy, setLynxBusy] = useState(false);
  const lynxSummary = useMemo(() => summarizeLynxSelection(lynxSelection), [lynxSelection]);
  const toggleLynx = (prueba) =>
    setLynxSelection((current) => {
      const next = new Map(current);
      if (next.has(prueba.id)) next.delete(prueba.id);
      else next.set(prueba.id, unitsForSelection(prueba, filters.jornada));
      return next;
    });
  const selectVisibleLynx = () =>
    setLynxSelection((current) => {
      const next = new Map(current);
      filtered.forEach((prueba) => {
        if (!lynxBlockReason(prueba) && !next.has(prueba.id)) next.set(prueba.id, unitsForSelection(prueba, filters.jornada));
      });
      return next;
    });
  const downloadLynxZip = async () => {
    if (!lynxSummary.ids.length || lynxBusy) return;
    setLynxBusy(true);
    try {
      const file = await downloadAthleticsLynxZip({ idevent, idsport, ids: lynxSummary.ids });
      saveAthleticsFile(file);
      toast.success(`Descargado: ${file.filename}`);
    } catch (error) {
      toast.error(parseAthleticsError(error).message);
    } finally {
      setLynxBusy(false);
    }
  };
  const lynxCount = `${lynxSummary.pruebas} ${lynxSummary.pruebas === 1 ? "prueba" : "pruebas"} · ${lynxSummary.series} ${
    lynxSummary.series === 1 ? "serie" : "series"
  }`;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="space-y-3 border-b border-slate-100 p-4">
        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={filters.query}
            onChange={(event) => setFilter("query", event.target.value)}
            placeholder="Buscar prueba (ej.: 100 av dam)"
            aria-label="Buscar prueba"
            className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-9 text-sm font-semibold text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
          />
          {filters.query ? (
            <button
              type="button"
              onClick={() => setFilter("query", "")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600"
              aria-label="Limpiar búsqueda"
            >
              <FiX aria-hidden="true" />
            </button>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <FilterSelect
            id="ath-filter-level"
            label="Nivel"
            value={filters.level}
            onChange={(value) => setFilter("level", value)}
            options={LEVEL_OPTIONS}
            allLabel="Todos"
          />
          <FilterSelect
            id="ath-filter-branch"
            label="Rama"
            value={filters.branch}
            onChange={(value) => setFilter("branch", value)}
            options={BRANCH_OPTIONS}
            allLabel="Todas"
          />
          <FilterSelect
            id="ath-filter-discipline"
            label="Tipo"
            value={filters.discipline}
            onChange={(value) => setFilter("discipline", value)}
            options={DISCIPLINE_OPTIONS}
            allLabel="Todos"
          />
          <FilterSelect
            id="ath-filter-status"
            label="Estado"
            value={filters.status}
            onChange={(value) => setFilter("status", value)}
            options={PRUEBA_STATUS_OPTIONS}
            allLabel="Todos"
          />
          <div className="col-span-2">
            <FilterSelect
              id="ath-filter-jornada"
              label="Jornada"
              value={filters.jornada}
              onChange={(value) => setFilter("jornada", value)}
              options={jornadaOptions}
              allLabel="Todas las jornadas"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 text-xs font-semibold text-slate-500">
          <span>
            {filtered.length} de {pruebas.length} pruebas
          </span>
          <span className="flex items-center gap-2">
            {activeFilters ? (
              <button
                type="button"
                onClick={() => onFiltersChange({ ...EMPTY_PRUEBA_FILTERS })}
                className="font-bold text-indigo-600 hover:text-indigo-800"
              >
                Limpiar filtros
              </button>
            ) : null}
            <IconAction
              icon={FiCheckSquare}
              tone="emerald"
              label={lynxMode ? "Ocultar la selección para FinishLynx" : "Seleccionar para FinishLynx"}
              aria-pressed={lynxMode}
              onClick={() => setLynxMode((current) => !current)}
              className={lynxMode ? "bg-emerald-50 !text-emerald-700" : ""}
            />
          </span>
        </div>

        {lynxMode ? (
          <div
            role="group"
            aria-label="FinishLynx de varias pruebas"
            className="space-y-1 rounded-lg border border-emerald-200 bg-emerald-50/60 px-2.5 py-1.5 text-xs"
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className="whitespace-nowrap font-bold text-emerald-900"
                title={filters.jornada ? "Con una jornada elegida se marcan solo las fases de esa jornada." : undefined}
              >
                {lynxCount}
              </span>
              <button
                type="button"
                onClick={downloadLynxZip}
                disabled={!lynxSummary.ids.length || lynxBusy}
                title="lynx.evt y lynx.sch con todas las pruebas marcadas, en orden de N° de horario"
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg bg-emerald-600 px-2 py-1 font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {lynxBusy ? <Spinner className="h-3 w-3" /> : <FiDownload aria-hidden="true" />} Descargar .zip
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={selectVisibleLynx} className="whitespace-nowrap font-bold text-emerald-700 hover:text-emerald-900">
                Seleccionar las visibles
              </button>
              {lynxSummary.pruebas ? (
                <button type="button" onClick={() => setLynxSelection(new Map())} className="whitespace-nowrap font-bold text-slate-500 hover:text-slate-700">
                  Ninguna
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {listQuery.isLoading ? (
          <LoadingState label="Cargando pruebas..." />
        ) : listQuery.isError ? (
          <ErrorState
            message={parseAthleticsError(listQuery.error).message}
            onRetry={() => listQuery.refetch()}
          />
        ) : !pruebas.length ? (
          <EmptyState
            title="No hay pruebas"
            description="Este evento aún no tiene pruebas de atletismo cargadas."
          />
        ) : !filtered.length ? (
          <EmptyState title="Sin coincidencias" description="Ninguna prueba coincide con la búsqueda o los filtros.">
            <button
              type="button"
              onClick={() => onFiltersChange({ ...EMPTY_PRUEBA_FILTERS })}
              className="mt-1 text-xs font-bold text-indigo-600 hover:text-indigo-800"
            >
              Limpiar filtros
            </button>
          </EmptyState>
        ) : (
          groups.map((group) => (
            <div key={group.key} className="mb-3">
              <div className="sticky top-0 z-[1] flex items-center justify-between bg-white/95 px-2 py-1.5 backdrop-blur">
                <span className="text-xs font-black uppercase tracking-wider text-indigo-700">{group.label}</span>
                <span className="text-[11px] font-bold text-slate-400">{group.pruebas.length}</span>
              </div>
              <div className="space-y-1">
                {group.pruebas.map((prueba) => (
                  <PruebaListItem
                    key={prueba.id}
                    prueba={prueba}
                    selected={Number(prueba.id) === Number(selectedId)}
                    onSelect={onSelect}
                    jornadaLabels={jornadaLabels}
                    expanded={expandedIds.has(Number(prueba.id))}
                    onToggle={toggleExpanded}
                    selectedSubId={selectedSubId}
                    onSelectSub={onSelectSub}
                    idevent={idevent}
                    idsport={idsport}
                    lynx={
                      lynxMode
                        ? { checked: lynxSelection.has(prueba.id), reason: lynxBlockReason(prueba), onToggle: toggleLynx }
                        : null
                    }
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PruebaListPanel;
